<script setup>
import { ref, watch, onMounted } from 'vue';
import Spinner from 'shared/components/Spinner.vue';
import InstagramMediaReportsAPI from 'dashboard/api/instagramMediaReports';

const props = defineProps({
  selectedInbox: { type: Object, default: null },
});

const isLoading = ref(false);
const isGenerating = ref(false);
const isSuggesting = ref(false);
const generatedContent = ref('');
const suggestedTopics = ref([]);
const botAnalysis = ref(null);
const isBotLoading = ref(false);

// Speech form
const speechType = ref('social_post');
const speechTopic = ref('');
const speechContext = ref('');
const speechTone = ref('formal');

// Survey form
const surveyTopic = ref('');
const surveyResult = ref(null);
const isGeneratingSurvey = ref(false);

const speechTypes = [
  { id: 'social_post', label: 'Post Redes Sociais' },
  { id: 'video_script', label: 'Roteiro de Vídeo/Reels' },
  { id: 'speech', label: 'Discurso' },
  { id: 'response', label: 'Resposta/Nota Oficial' },
  { id: 'press_release', label: 'Nota para Imprensa' },
  { id: 'debate_prep', label: 'Preparação para Debate' },
];

const tones = [
  { id: 'formal', label: 'Formal' },
  { id: 'combativo', label: 'Combativo' },
  { id: 'emocional', label: 'Emocional' },
  { id: 'tecnico', label: 'Técnico' },
  { id: 'popular', label: 'Popular/Informal' },
];

async function generateSpeech() {
  if (!props.selectedInbox || !speechTopic.value) return;
  isGenerating.value = true;
  generatedContent.value = '';
  try {
    const { data } = await InstagramMediaReportsAPI.generateSpeech({
      inboxId: props.selectedInbox.id,
      type: speechType.value,
      topic: speechTopic.value,
      context: speechContext.value,
      tone: speechTone.value,
    });
    generatedContent.value = data.content || data.error;
  } catch (e) {
    generatedContent.value = 'Erro ao gerar conteúdo.';
  } finally {
    isGenerating.value = false;
  }
}

async function fetchSuggestions() {
  if (!props.selectedInbox) return;
  isSuggesting.value = true;
  try {
    const { data } = await InstagramMediaReportsAPI.suggestTopics({
      inboxId: props.selectedInbox.id,
    });
    suggestedTopics.value = data;
  } catch (e) {
    console.error(e);
  } finally {
    isSuggesting.value = false;
  }
}

async function generateSurvey() {
  if (!props.selectedInbox || !surveyTopic.value) return;
  isGeneratingSurvey.value = true;
  try {
    const { data } = await InstagramMediaReportsAPI.generateSurvey({
      inboxId: props.selectedInbox.id,
      topic: surveyTopic.value,
      count: 5,
    });
    surveyResult.value = data;
  } catch (e) {
    console.error(e);
  } finally {
    isGeneratingSurvey.value = false;
  }
}

async function fetchBotDetection() {
  if (!props.selectedInbox) return;
  isBotLoading.value = true;
  try {
    const { data } = await InstagramMediaReportsAPI.getBotDetection({
      inboxId: props.selectedInbox.id,
    });
    botAnalysis.value = data;
  } catch (e) {
    console.error(e);
  } finally {
    isBotLoading.value = false;
  }
}

function useSuggestion(topic) {
  speechTopic.value = topic.topic;
  speechTone.value = topic.suggested_tone || 'formal';
}

function copyContent() {
  navigator.clipboard.writeText(generatedContent.value);
}

watch(() => props.selectedInbox, () => {
  fetchSuggestions();
  fetchBotDetection();
});
onMounted(() => {
  fetchSuggestions();
  fetchBotDetection();
});
</script>

<template>
  <div class="p-4 space-y-6">
    <h2 class="text-xl font-bold text-n-slate-12">Assistente IA & Inteligência</h2>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
      <!-- Speech Generator -->
      <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
        <h3 class="text-lg font-semibold mb-4 text-n-slate-12">Gerador de Conteúdo</h3>
        <div class="space-y-3">
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="text-xs text-n-slate-11 mb-1 block">Tipo</label>
              <select v-model="speechType" class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-3 border-n-weak text-n-slate-12">
                <option v-for="t in speechTypes" :key="t.id" :value="t.id">{{ t.label }}</option>
              </select>
            </div>
            <div>
              <label class="text-xs text-n-slate-11 mb-1 block">Tom</label>
              <select v-model="speechTone" class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-3 border-n-weak text-n-slate-12">
                <option v-for="t in tones" :key="t.id" :value="t.id">{{ t.label }}</option>
              </select>
            </div>
          </div>
          <div>
            <label class="text-xs text-n-slate-11 mb-1 block">Tema</label>
            <input v-model="speechTopic" type="text" class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-3 border-n-weak text-n-slate-12" placeholder="Ex: segurança pública em Goiânia" />
          </div>
          <div>
            <label class="text-xs text-n-slate-11 mb-1 block">Contexto (opcional)</label>
            <textarea v-model="speechContext" rows="2" class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-3 border-n-weak text-n-slate-12 resize-none" placeholder="Contexto adicional..." />
          </div>
          <button @click="generateSpeech" :disabled="isGenerating || !speechTopic" class="w-full py-2 px-4 rounded-lg bg-n-blue-9 text-white text-sm font-medium disabled:opacity-50">
            <Spinner v-if="isGenerating" size="small" />
            <span v-else>Gerar Conteúdo</span>
          </button>
        </div>

        <div v-if="generatedContent" class="mt-4 p-3 bg-n-solid-3 rounded-lg border border-n-weak">
          <div class="flex justify-between items-center mb-2">
            <span class="text-xs font-medium text-n-slate-11">Resultado</span>
            <button @click="copyContent" class="text-xs text-n-blue-11 hover:underline">Copiar</button>
          </div>
          <pre class="text-sm text-n-slate-12 whitespace-pre-wrap font-sans">{{ generatedContent }}</pre>
        </div>
      </div>

      <!-- Suggested Topics -->
      <div class="space-y-6">
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold text-n-slate-12">Temas Urgentes</h3>
            <button @click="fetchSuggestions" :disabled="isSuggesting" class="text-xs text-n-blue-11 hover:underline">Atualizar</button>
          </div>
          <Spinner v-if="isSuggesting" size="small" />
          <div v-else-if="suggestedTopics.length" class="space-y-2">
            <div v-for="(topic, idx) in suggestedTopics" :key="idx"
              class="p-2 rounded border border-n-weak/50 cursor-pointer hover:bg-n-solid-3 transition-colors"
              @click="useSuggestion(topic)">
              <div class="flex items-center gap-2">
                <span class="text-xs px-1.5 py-0.5 rounded-full text-white"
                  :class="topic.urgency === 'alta' ? 'bg-n-ruby-9' : topic.urgency === 'media' ? 'bg-n-amber-9' : 'bg-n-slate-9'">
                  {{ topic.urgency }}
                </span>
                <span class="text-sm font-medium text-n-slate-12">{{ topic.topic }}</span>
              </div>
              <p class="text-xs text-n-slate-11 mt-1">{{ topic.reason }}</p>
            </div>
          </div>
          <p v-else class="text-sm text-n-slate-11">Nenhuma sugestão disponível</p>
        </div>

        <!-- Pesquisa WhatsApp -->
        <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
          <h3 class="text-lg font-semibold mb-4 text-n-slate-12">Pesquisa via WhatsApp</h3>
          <div class="space-y-3">
            <input v-model="surveyTopic" type="text" class="w-full py-1.5 px-3 text-sm rounded-lg border bg-n-solid-3 border-n-weak text-n-slate-12" placeholder="Tema da pesquisa..." />
            <button @click="generateSurvey" :disabled="isGeneratingSurvey || !surveyTopic" class="w-full py-2 px-4 rounded-lg bg-n-violet-9 text-white text-sm font-medium disabled:opacity-50">
              <Spinner v-if="isGeneratingSurvey" size="small" />
              <span v-else>Gerar Pesquisa</span>
            </button>
          </div>
          <div v-if="surveyResult?.questions?.length" class="mt-3 space-y-2">
            <div v-for="(q, idx) in surveyResult.questions" :key="idx" class="p-2 bg-n-solid-3 rounded text-sm">
              <p class="font-medium text-n-slate-12">{{ idx + 1 }}. {{ q.question }}</p>
              <div v-if="q.options" class="mt-1 space-y-0.5">
                <p v-for="(opt, j) in q.options" :key="j" class="text-xs text-n-slate-11 ml-3">{{ String.fromCharCode(65 + j) }}) {{ opt }}</p>
              </div>
              <span class="text-[10px] text-n-slate-9">{{ q.type }} | {{ q.objective }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bot Detection -->
    <div class="bg-n-solid-2 rounded-lg p-4 border border-n-weak">
      <h3 class="text-lg font-semibold mb-4 text-n-slate-12">Detecção de Bots & Clusters Coordenados</h3>
      <Spinner v-if="isBotLoading" size="small" />
      <template v-else-if="botAnalysis">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div class="p-3 rounded-lg border border-n-weak text-center">
            <p class="text-xs text-n-slate-11">Score de Risco</p>
            <p class="text-2xl font-bold" :class="botAnalysis.risk_score?.level === 'critical' ? 'text-n-ruby-11' : botAnalysis.risk_score?.level === 'high' ? 'text-n-orange-11' : 'text-n-teal-11'">
              {{ botAnalysis.risk_score?.score || 0 }}/100
            </p>
            <span class="text-xs px-2 py-0.5 rounded-full text-white"
              :class="botAnalysis.risk_score?.level === 'critical' ? 'bg-n-ruby-9' : botAnalysis.risk_score?.level === 'high' ? 'bg-n-orange-9' : botAnalysis.risk_score?.level === 'medium' ? 'bg-n-amber-9' : 'bg-n-teal-9'">
              {{ botAnalysis.risk_score?.level }}
            </span>
          </div>
          <div class="p-3 rounded-lg border border-n-weak text-center">
            <p class="text-xs text-n-slate-11">Clusters</p>
            <p class="text-2xl font-bold text-n-slate-12">{{ botAnalysis.coordinated_clusters?.length || 0 }}</p>
          </div>
          <div class="p-3 rounded-lg border border-n-weak text-center">
            <p class="text-xs text-n-slate-11">Padrões</p>
            <p class="text-2xl font-bold text-n-slate-12">{{ botAnalysis.suspicious_patterns?.length || 0 }}</p>
          </div>
          <div class="p-3 rounded-lg border border-n-weak text-center">
            <p class="text-xs text-n-slate-11">Surtos</p>
            <p class="text-2xl font-bold text-n-slate-12">{{ botAnalysis.burst_activity?.length || 0 }}</p>
          </div>
        </div>

        <div v-if="botAnalysis.suspicious_patterns?.length" class="mb-4">
          <h4 class="text-sm font-medium text-n-slate-12 mb-2">Padrões Suspeitos</h4>
          <div v-for="(p, idx) in botAnalysis.suspicious_patterns" :key="idx" class="p-2 mb-1 rounded border border-n-weak/50 text-xs">
            <span class="px-1.5 py-0.5 rounded-full text-white mr-2" :class="p.severity === 'high' ? 'bg-n-ruby-9' : 'bg-n-amber-9'">{{ p.severity }}</span>
            {{ p.description }}
          </div>
        </div>

        <div v-if="botAnalysis.burst_activity?.length">
          <h4 class="text-sm font-medium text-n-slate-12 mb-2">Atividade em Surto</h4>
          <div v-for="(b, idx) in botAnalysis.burst_activity" :key="idx" class="p-2 mb-1 rounded border border-n-weak/50 text-xs">
            <span class="px-1.5 py-0.5 rounded-full text-white mr-2" :class="b.severity === 'critical' ? 'bg-n-ruby-9' : 'bg-n-orange-9'">{{ b.severity }}</span>
            {{ b.description }}
          </div>
        </div>

        <p v-if="!botAnalysis.suspicious_patterns?.length && !botAnalysis.burst_activity?.length" class="text-sm text-n-teal-11">Nenhuma atividade suspeita detectada nos últimos 7 dias.</p>
      </template>
    </div>
  </div>
</template>
